import React, { useState } from 'react';
import BusinessKitzLogo from '../../../assets/svg/business-kitz-logo.svg?react';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import CottageOutlinedIcon from '@mui/icons-material/CottageOutlined';
import LinkItem from './components/LinkItem/LInkItem';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import { useDocumensoRedirect } from '../hooks/useDocumensoRedirect';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PackageIcon from '../../../assets/svg/menu/package-2.svg?react';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { ROUTES } from '../constants/routes';
import { useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useAppSelector } from '../hooks/useAppSelector';
import { RolesGate } from '../roles-gate';
import { GROUPS } from '../constants/groups';
import { selectUser, selectUserRoleByGroupName } from '../../slices/user/userSlice';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { revertAll } from '../../store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import CalendarIcon from '../../../assets/svg/calendar_month.svg?react';
import ContractEditIcon from '../../../assets/svg/menu/contract-edit.svg?react';
import LogoutIcon from '../../../assets/svg/menu/logout.svg?react';
import BusinessCenterIcon from '../../../assets/svg/menu/business-center.svg?react';
import { Box, Button } from '@mui/material';

const MainNavigation = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { signOut } = useClerk();
  const path = location.pathname;
  const currentUser = useAppSelector(selectUser);
  const userIsEmployee = Boolean(
    useAppSelector(selectUserRoleByGroupName(GROUPS.COMPANY_EMPLOYEE))
  );
  const employees = useAppSelector(selectAllEmployees);
  const employeeId =
    userIsEmployee &&
    employees.find(
      (employee) =>
        employee.email_address?.toLowerCase() === currentUser.email ||
        employee.business_email_address?.toLowerCase() === currentUser.email
    )?.id;
  const { redirectToDocumenso } = useDocumensoRedirect();
  const [expandedView, setExpandedView] = useState<boolean>(false);
  return (
    <>
      {expandedView && (
        <div
          id='mainNavigationCloseExpandedViewArea'
          tabIndex={0}
          role='button'
          className='navigation-under-layer'
          onClick={() => setExpandedView(false)}
          onKeyDown={() => {
            return;
          }}
        />
      )}
      <nav className={'main-navigation' + (expandedView ? ' expanded-view' : '')}>
        {expandedView ? (
          <div className='logo-wrapper'>
            <BusinessKitzLogo />
          </div>
        ) : (
          <div
            id='mainNavigationOpenExpandedViewArea'
            role='button'
            tabIndex={0}
            className='menu-control-wrapper'
            onClick={() => setExpandedView(true)}
            onKeyDown={() => {
              return;
            }}
          >
            <MenuOutlinedIcon id='mainNavigationOpenExpandedViewAreaMenuIcon' fontSize='large' />
          </div>
        )}
        <Box component='ul' sx={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
          <li>
            <LinkItem
              id='mainNavigationHomeNavLink'
              url={ROUTES.HOME}
              active={path === ROUTES.HOME}
              expandedView={expandedView}
              linkText={t('menu.home')}
              onClick={() => setExpandedView(false)}
            >
              <CottageOutlinedIcon id='mainNavigationHomeNavLinkCottageIcon' fontSize='medium' />
            </LinkItem>
          </li>
        </Box>
        {!userIsEmployee && <div className='section-label'>{t('menu.documents')}</div>}
        <Box
          component='ul'
          className='with-border'
          sx={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}
        >
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              <LinkItem
                id='mainNavigationDocumentLibraryNavLink'
                url={ROUTES.DOCUMENT_LIBRARY}
                active={path.includes(ROUTES.DOCUMENT_LIBRARY)}
                expandedView={expandedView}
                linkText={t('menu.library')}
                onClick={() => setExpandedView(false)}
              >
                <AccountBalanceOutlinedIcon
                  id='mainNavigationDocumentLibraryNavLinkAccountBalanceIcon'
                  fontSize='medium'
                />
              </LinkItem>
            </li>
          </RolesGate>
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li className='key-dates-link'>
              <LinkItem
                id='mainNavigationDocumentsKeyDatesNavLink'
                url={ROUTES.DOCUMENTS_KEY_DATES}
                active={path.includes(ROUTES.DOCUMENTS_KEY_DATES)}
                expandedView={expandedView}
                linkText={t('menu.keyDates')}
                onClick={() => setExpandedView(false)}
              >
                <CalendarIcon id='mainNavigationDocumentsKeyDatesNavLinkCalendarIcon' />
              </LinkItem>
            </li>
          </RolesGate>
          {/*<li>
            <LinkItem
              id='mainNavigationWorkspaceNavLink'
              url={ROUTES.WORKSPACE}
              active={path.includes(ROUTES.WORKSPACE)}
              onClick={() => setExpandedView(false)}
              expandedView={expandedView}
              linkText={t('menu.workspace')}
            >
              <ClarifyIcon id='mainNavigationWorkspaceNavLinkClarifyIcon' />
            </LinkItem>
          </li>*/}
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              <LinkItem
                id='mainNavigationDocumentVaultNavLink'
                url={ROUTES.DOCUMENT_VAULT}
                active={path.includes(ROUTES.DOCUMENT_VAULT)}
                expandedView={expandedView}
                linkText={t('menu.vault')}
                onClick={() => setExpandedView(false)}
              >
                <PackageIcon id='mainNavigationDocumentVaultNavLinkPackageIcon' />
              </LinkItem>
            </li>
          </RolesGate>
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              {expandedView ? (
                <Button
                  id='mainNavigationSignBtn'
                  className='main-nav-link'
                  onClick={() => {
                    setExpandedView(false);
                    redirectToDocumenso('/documents').catch(() => {
                      toast.error(t('messages.failedDocumensoRedirect'));
                    });
                  }}
                >
                  <ContractEditIcon id='mainNavigationSignBtnContractEditIcon' />
                  <span id='mainNavigationSignBtnMainNavLinkText' className='main-nav-link-text'>
                    {t('menu.sign')}
                  </span>
                </Button>
              ) : (
                <Tooltip title={t('menu.sign')} placement='top' arrow>
                  <Button
                    id='mainNavigationSignBtn2'
                    className='main-nav-link'
                    onClick={() => {
                      setExpandedView(false);
                      redirectToDocumenso('/documents').catch(() => {
                        toast.error(t('messages.failedDocumensoRedirect'));
                      });
                    }}
                  >
                    <ContractEditIcon id='mainNavigationSignBtn2ContractEditIcon' />
                  </Button>
                </Tooltip>
              )}
            </li>
          </RolesGate>
        </Box>
        {!userIsEmployee && <div className='section-label'>{t('menu.profiles')}</div>}
        <Box component='ul' sx={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              <LinkItem
                id='mainNavigationBusinessProfileNavLink'
                url={ROUTES.BUSINESS_PROFILE}
                active={path.includes(ROUTES.BUSINESS_PROFILE)}
                expandedView={expandedView}
                linkText={t('menu.business')}
                onClick={() => setExpandedView(false)}
              >
                <ApartmentOutlinedIcon
                  id='mainNavigationBusinessProfileNavLinkApartmentIcon'
                  fontSize='medium'
                />
              </LinkItem>
            </li>
          </RolesGate>
          <li>
            <LinkItem
              id='mainNavigationEmployeesNavLink'
              url={
                userIsEmployee && employeeId
                  ? `${ROUTES.EMPLOYEE_PROFILES}/${employeeId}`
                  : ROUTES.EMPLOYEE_PROFILES
              }
              active={path.includes(ROUTES.EMPLOYEE_PROFILES)}
              expandedView={expandedView}
              linkText={userIsEmployee ? t('buttons.employee') : t('menu.employees')}
              onClick={() => setExpandedView(false)}
            >
              <BadgeOutlinedIcon id='mainNavigationEmployeesNavLinkBadgeIcon' fontSize='medium' />
            </LinkItem>
          </li>
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              <LinkItem
                id='mainNavigationCustomerProfilesNavLink'
                url={ROUTES.CUSTOMER_PROFILES}
                active={path.includes(ROUTES.CUSTOMER_PROFILES)}
                expandedView={expandedView}
                linkText={t('menu.customers')}
                onClick={() => setExpandedView(false)}
              >
                <SupportAgentOutlinedIcon
                  id='mainNavigationCustomerProfilesNavLinkSupportAgentIcon'
                  fontSize='medium'
                />
              </LinkItem>
            </li>
          </RolesGate>
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              <LinkItem
                id='mainNavigationSupplierProfilesNavLink'
                url={ROUTES.SUPPLIER_PROFILES}
                active={path.includes(ROUTES.SUPPLIER_PROFILES)}
                expandedView={expandedView}
                linkText={t('menu.suppliers')}
                onClick={() => setExpandedView(false)}
              >
                <BusinessCenterIcon id='mainNavigationSupplierProfilesNavLinkBusinessCenterIcon' />
              </LinkItem>
            </li>
          </RolesGate>
        </Box>
        <RolesGate group={GROUPS.COMPANY_ADMIN}>
          <Box
            component='ul'
            className='with-border'
            sx={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0, marginTop: 'auto' }}
          >
            <li>
              <LinkItem
                id='mainNavigationPricingNavLink'
                url={ROUTES.PRICING}
                active={path.includes(ROUTES.PRICING)}
                expandedView={expandedView}
                linkText={t('menu.plans')}
                onClick={() => setExpandedView(false)}
              >
                <SellOutlinedIcon id='mainNavigationPricingNavLinkSellIcon' fontSize='medium' />
              </LinkItem>
            </li>
          </Box>
        </RolesGate>
        <Box component='ul' sx={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
          <RolesGate group={GROUPS.COMPANY_ADMIN}>
            <li>
              <LinkItem
                id='mainNavigationSettingsNavLink'
                url={ROUTES.SETTINGS}
                active={path.includes(ROUTES.SETTINGS)}
                expandedView={expandedView}
                linkText={t('menu.settings')}
                onClick={() => setExpandedView(false)}
              >
                <SettingsOutlinedIcon
                  id='mainNavigationSettingsNavLinkSettingsIcon'
                  fontSize='medium'
                />
              </LinkItem>
            </li>
          </RolesGate>
          <li>
            {expandedView ? (
              <span
                id='mainNavigationLogoutText'
                className='main-nav-link'
                role='button'
                tabIndex={-1}
                onKeyUp={() => {
                  return;
                }}
                onClick={() => signOut({ redirectUrl: ROUTES.LOGIN })}
              >
                <LogoutIcon id='mainNavigationLogoutTextLogoutIcon' />
                <span id='mainNavigationLogoutTextMainNavLinkText' className='main-nav-link-text'>
                  {t('menu.logout')}
                </span>
              </span>
            ) : (
              <Tooltip title={t('menu.logout')} placement='top' arrow>
                <span
                  id='mainNavigationLogoutText2'
                  className='main-nav-link'
                  role='button'
                  tabIndex={-1}
                  onKeyUp={() => {
                    return;
                  }}
                  onClick={() => {
                    signOut({ redirectUrl: ROUTES.LOGIN }).catch();
                    dispatch(revertAll());
                  }}
                >
                  <LogoutIcon id='mainNavigationLogoutText2LogoutIcon' />
                </span>
              </Tooltip>
            )}
          </li>
        </Box>
      </nav>
    </>
  );
};

export default MainNavigation;
